import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { ticketAPI, commentAPI, getAttachmentUrl } from '../services/api';
import { FiArrowLeft, FiCalendar, FiUser, FiAlertCircle, FiCheck, FiX, FiMessageSquare, FiClock, FiMonitor, FiSmartphone, FiTag, FiFilm, FiPhone } from 'react-icons/fi';
import './TicketDetail.css';

const TicketDetail = () => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');

  // Comments
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  // Update History
  const [updateHistory, setUpdateHistory] = useState([]);
  const [showUpdateHistory, setShowUpdateHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Edit Ticket
  const [showEditModal, setShowEditModal] = useState(false);
  const [editComplaintType, setEditComplaintType] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [complaintTypes, setComplaintTypes] = useState([]);

  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTicket();
    fetchComments();
    fetchComplaintTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchComplaintTypes = async () => {
    try {
      const response = await api.get('/complaint-types');
      setComplaintTypes(response.data);
    } catch (error) {
      console.error('Error fetching complaint types:', error);
    }
  };

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const response = await ticketAPI.getTicket(id);
      setTicket(response.data);
    } catch (error) {
      setError('Failed to load ticket details');
      console.error('Error fetching ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      setLoadingComments(true);
      const response = await commentAPI.getComments(id);
      // Transform comments to flatten user data
      const transformedComments = response.data.map(comment => ({
        ...comment,
        user_name: comment.user?.full_name || comment.user?.username || 'Unknown User'
      }));
      setComments(transformedComments);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await commentAPI.addComment(id, {
        user_id: user.id,
        comment: newComment
      });
      setNewComment('');
      fetchComments();
      setSuccess('Comment added successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add comment');
    }
  };

  const fetchUpdateHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await ticketAPI.getUpdateHistory(id);
      setUpdateHistory(response.data.updates);
      setShowUpdateHistory(true);
    } catch (error) {
      console.error('Error fetching update history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm('Are you sure you want to withdraw this ticket? This action cannot be undone.')) {
      return;
    }

    try {
      setUpdating(true);
      setError('');
      await ticketAPI.withdrawTicket(id, { user_id: user.id });
      setSuccess('Ticket withdrawn successfully');
      fetchTicket();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to withdraw ticket');
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenEditModal = () => {
    setEditComplaintType(ticket.complaint_type_id);
    setEditNotes('');
    setShowEditModal(true);
  };

  const handleEditTicket = async () => {
    if (!editComplaintType) {
      setError('Please select a complaint type');
      return;
    }

    try {
      setUpdating(true);
      setError('');

      await ticketAPI.updateTicket(id, {
        complaint_type_id: editComplaintType,
        updated_by: user.id,
        notes: editNotes
      });

      setSuccess('Complaint type updated successfully');
      setShowEditModal(false);
      fetchTicket();
      fetchUpdateHistory();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update complaint type');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      setError('Please select a status');
      return;
    }

    try {
      setUpdating(true);
      setError('');
      await ticketAPI.updateStatus(id, {
        status: newStatus,
        changed_by: user.id,
        notes: statusNotes,
      });
      setSuccess('Status updated successfully');
      setShowStatusModal(false);
      setNewStatus('');
      setStatusNotes('');
      fetchTicket();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate time elapsed for a ticket
  const calculateTimeElapsed = (createdAt, status, statusHistory) => {
    const now = new Date();
    const created = new Date(createdAt);

    // For resolved/closed tickets, find when they were resolved/closed
    if (status === 'resolved' || status === 'closed') {
      const resolvedHistory = statusHistory?.find(h => h.new_status === 'resolved' || h.new_status === 'closed');
      if (resolvedHistory) {
        const resolvedDate = new Date(resolvedHistory.changed_at);
        const diffMs = resolvedDate - created;
        return formatTimeDifference(diffMs);
      }
    }

    // For open/in-progress tickets, calculate from creation time to now
    if (status === 'open' || status === 'in-progress') {
      const diffMs = now - created;
      return formatTimeDifference(diffMs);
    }

    return null;
  };

  // Format time difference
  const formatTimeDifference = (diffMs) => {
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      const remainingHours = diffHours % 24;
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      const remainingMinutes = diffMinutes % 60;
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
    } else {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
    }
  };

  const getStatusBadge = (status, isWithdrawn) => {
    if (isWithdrawn) return 'badge-withdrawn';
    switch (status) {
      case 'open':
        return 'badge-open';
      case 'in-progress':
        return 'badge-in-progress';
      case 'resolved':
        return 'badge-resolved';
      case 'closed':
        return 'badge-closed';
      default:
        return 'badge-open';
    }
  };

  if (loading) {
    return (
      <div className="ticket-detail-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ticket-detail-container">
        <div className="container">
          <div className="error-message">Ticket not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-detail-container">
      <div className="container">
        <div className="detail-header">
          <button onClick={() => navigate('/dashboard')} className="back-button">
            <FiArrowLeft /> Back to Dashboard
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="detail-content">
          <div className="detail-main">
            <div className={`detail-card card fade-in ${ticket.is_withdrawn ? 'withdrawn' : ''}`}>
              <div className="ticket-status-header">
                <span className={`ticket-id-large ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                  {ticket.ticket_id}
                </span>
                <span className={`badge ${getStatusBadge(ticket.status, ticket.is_withdrawn)}`}>
                  {ticket.is_withdrawn ? 'Withdrawn' : ticket.status}
                </span>
              </div>

              <h1 className={`detail-title ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                {ticket.title}
              </h1>

              <div className="detail-meta-grid">
                <div className="meta-card">
                  <FiUser className="meta-icon" />
                  <div>
                    <div className="meta-label">Created By</div>
                    <div className="meta-value">{ticket.user_name}</div>
                  </div>
                </div>

                {ticket.is_student_complaint && ticket.student_phone && (
                  <div className="meta-card">
                    <FiPhone className="meta-icon" />
                    <div>
                      <div className="meta-label">Student Phone</div>
                      <div className="meta-value">{ticket.student_phone}</div>
                    </div>
                  </div>
                )}

                <div className="meta-card">
                  <FiCalendar className="meta-icon" />
                  <div>
                    <div className="meta-label">Created On</div>
                    <div className="meta-value">{formatDate(ticket.created_at)}</div>
                  </div>
                </div>

                <div className="meta-card">
                  <FiAlertCircle className="meta-icon" />
                  <div>
                    <div className="meta-label">Complaint Type</div>
                    <div className="meta-value">{ticket.complaint_type_name}</div>
                  </div>
                </div>

                {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
                  <div className="meta-card">
                    <FiCalendar className="meta-icon" />
                    <div>
                      <div className="meta-label">Last Updated</div>
                      <div className="meta-value">{formatDate(ticket.updated_at)}</div>
                    </div>
                  </div>
                )}

                {ticket.os_version && (
                  <div className="meta-card">
                    <FiMonitor className="meta-icon" />
                    <div>
                      <div className="meta-label">OS Type</div>
                      <div className="meta-value">{ticket.os_version}</div>
                    </div>
                  </div>
                )}

                {ticket.phone_model && (
                  <div className="meta-card">
                    <FiSmartphone className="meta-icon" />
                    <div>
                      <div className="meta-label">Device Type</div>
                      <div className="meta-value">{ticket.phone_model}</div>
                    </div>
                  </div>
                )}

                {ticket.complaint_category && (
                  <div className="meta-card">
                    <FiTag className="meta-icon" />
                    <div>
                      <div className="meta-label">Complaint Type</div>
                      <div className="meta-value">{ticket.complaint_category}</div>
                    </div>
                  </div>
                )}

                {/* Time Tracking Information */}
                {(ticket.status === 'open' || ticket.status === 'in-progress') && (
                  <div className="meta-card time-tracking-card open">
                    <FiClock className="meta-icon" />
                    <div>
                      <div className="meta-label">Time Elapsed</div>
                      <div className="meta-value time-elapsed-value">
                        {calculateTimeElapsed(ticket.created_at, ticket.status, ticket.status_history)}
                      </div>
                      <div className="meta-label-small">Ticket is currently {ticket.status}</div>
                    </div>
                  </div>
                )}

                {(ticket.status === 'resolved' || ticket.status === 'closed') && (
                  <div className="meta-card time-tracking-card resolved">
                    <FiClock className="meta-icon" />
                    <div>
                      <div className="meta-label">Resolution Time</div>
                      <div className="meta-value time-resolved-value">
                        {calculateTimeElapsed(ticket.created_at, ticket.status, ticket.status_history)}
                      </div>
                      <div className="meta-label-small">Ticket was {ticket.status}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h2 className="section-title">Description</h2>
                <p className={`detail-description ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                  {ticket.description}
                </p>
              </div>

              {ticket.attachments && ticket.attachments.length > 0 && (
                <div className="detail-section">
                  <h2 className="section-title">Attachments</h2>
                  <div className="attachments-grid">
                    {ticket.attachments.map((attachment) => (
                      <a
                        key={attachment.id}
                        href={getAttachmentUrl(attachment.file_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="attachment-card"
                      >
                        {attachment.file_type?.startsWith('image/') ? (
                          <img
                            src={getAttachmentUrl(attachment.file_path)}
                            alt={attachment.file_name}
                            className="attachment-image"
                          />
                        ) : (
                          <div className="attachment-video-icon"><FiFilm /></div>
                        )}
                        <div className="attachment-info">
                          <span className="attachment-name">{attachment.file_name}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {!ticket.is_withdrawn && (
                <div className="detail-actions">
                  {/* Only ticket creator can withdraw */}
                  {ticket.user_id === user.id && (
                    <button onClick={handleWithdraw} className="btn btn-secondary" disabled={updating}>
                      <FiX /> Withdraw Ticket
                    </button>
                  )}
                  {/* Admin can change complaint type */}
                  {user.role === 'admin' && (
                    <button onClick={handleOpenEditModal} className="btn btn-secondary" disabled={updating}>
                      <FiAlertCircle /> Change Type
                    </button>
                  )}
                  {/* Admin, staff, or ticket creator can update status */}
                  {(user.role === 'admin' || user.role === 'staff' || ticket.user_id === user.id) && (
                    <button
                      onClick={() => setShowStatusModal(true)}
                      className="btn btn-primary"
                      disabled={updating}
                    >
                      <FiCheck /> Update Status
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="detail-sidebar">
            {ticket.status_history && ticket.status_history.length > 0 && (
              <div className="history-card card">
                <h2 className="section-title">Status History</h2>
                <div className="history-timeline">
                  {ticket.status_history.map((history) => (
                    <div key={history.id} className="history-item">
                      <div className="history-dot"></div>
                      <div className="history-content">
                        <div className="history-status">
                          {history.old_status && (
                            <span className="status-change">
                              {history.old_status} → {history.new_status}
                            </span>
                          )}
                          {!history.old_status && (
                            <span className="status-change">{history.new_status}</span>
                          )}
                        </div>
                        <div className="history-meta">
                          <span className="history-user">{history.changed_by}</span>
                          <span className="history-date">{formatDate(history.changed_at)}</span>
                        </div>
                        {history.notes && <p className="history-notes">{history.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Comments Section */}
        <div className="ticket-detail-card" style={{ marginTop: '24px' }}>
          <h2 className="section-title">
            <FiMessageSquare /> Comments ({comments.length})
          </h2>

          {loadingComments ? (
            <div>Loading comments...</div>
          ) : (
            <>
              {comments.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  {comments.map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        borderLeft: comment.is_internal ? '3px solid #f59e0b' : '3px solid var(--primary)'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        fontSize: '14px'
                      }}>
                        <strong>{comment.user_name}</strong>
                        {comment.is_internal && (
                          <span className="badge badge-in-progress" style={{ fontSize: '11px' }}>
                            Internal
                          </span>
                        )}
                      </div>
                      <p style={{ margin: '8px 0', lineHeight: '1.6' }}>{comment.comment}</p>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {formatDate(comment.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={handleAddComment}>
                <div className="form-group">
                  <label className="form-label">Add Comment</label>
                  <textarea
                    className="form-textarea"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Type your comment here..."
                    rows={4}
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!newComment.trim()}
                >
                  <FiMessageSquare /> Post Comment
                </button>
              </form>
            </>
          )}
        </div>

        {/* Update History Section */}
        <div className="ticket-detail-card" style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 className="section-title" style={{ margin: 0 }}>
              <FiClock /> Field Update History
            </h2>
            <button
              onClick={fetchUpdateHistory}
              className="btn btn-secondary"
              disabled={loadingHistory}
            >
              {loadingHistory ? 'Loading...' : showUpdateHistory ? 'Refresh' : 'View History'}
            </button>
          </div>

          {showUpdateHistory && (
            <div>
              {updateHistory.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '20px' }}>
                  No field updates recorded yet.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {updateHistory.map((update) => (
                    <div
                      key={update.id}
                      style={{
                        background: 'var(--bg-secondary)',
                        padding: '16px',
                        borderRadius: '8px',
                        borderLeft: '3px solid var(--primary)'
                      }}
                    >
                      <div style={{ marginBottom: '8px' }}>
                        <strong>{update.updated_by_name}</strong>
                        <span style={{ color: 'var(--text-secondary)' }}> changed </span>
                        <span style={{
                          background: 'rgba(59, 130, 246, 0.1)',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontWeight: '500'
                        }}>
                          {update.field_name}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        margin: '12px 0',
                        fontSize: '14px'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '4px' }}>
                            From:
                          </div>
                          <div style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            padding: '8px',
                            borderRadius: '4px'
                          }}>
                            {update.old_value || '(empty)'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>→</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '4px' }}>
                            To:
                          </div>
                          <div style={{
                            background: 'rgba(16, 185, 129, 0.1)',
                            padding: '8px',
                            borderRadius: '4px'
                          }}>
                            {update.new_value}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {formatDate(update.updated_at)}
                      </div>
                      {update.notes && (
                        <div style={{
                          marginTop: '8px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-secondary)'
                        }}>
                          Note: {update.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showStatusModal && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Update Status</h2>
              <button onClick={() => setShowStatusModal(false)} className="modal-close">
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New Status</label>
                <select
                  className="form-select"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="">Select status</option>
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Notes (Optional)</label>
                <textarea
                  className="form-textarea"
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Add notes about this status change..."
                  rows={4}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowStatusModal(false)}
                className="btn btn-secondary"
                disabled={updating}
              >
                Cancel
              </button>
              <button onClick={handleStatusUpdate} className="btn btn-primary" disabled={updating}>
                {updating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Complaint Type</h2>
              <button onClick={() => setShowEditModal(false)} className="modal-close">
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Current Complaint Type</label>
                <input
                  type="text"
                  className="form-input disabled-input"
                  value={ticket.complaint_type_name}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">New Complaint Type *</label>
                <select
                  className="form-select"
                  value={editComplaintType}
                  onChange={(e) => setEditComplaintType(e.target.value)}
                >
                  <option value="">Select complaint type</option>
                  {complaintTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.type_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Reason for Change (Optional)</label>
                <textarea
                  className="form-textarea"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Explain why you're changing the complaint type..."
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
                disabled={updating}
              >
                Cancel
              </button>
              <button onClick={handleEditTicket} className="btn btn-primary" disabled={updating}>
                {updating ? 'Updating...' : 'Change Complaint Type'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail;
